// ============================================================
// subscription-management.component.ts
// ============================================================

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  SubscriptionPlanService,
  AdminPlan,
  PlatformOfferDoc,
} from './subscription-services/subscription-plan.service';

export type { AdminPlan };

export interface PlanStats {
  mrr: number;
  totalVendors: number;
  freeVendors: number;
  paidVendors: number;
  vendorsNearLimit: number;
  gracePeriodAccounts: number;
}

export type PlatformOffer = PlatformOfferDoc;

const OFFER_DEFAULTS: PlatformOffer = {
  enabled: false,
  badgeText: 'Limited Offer',
  highlightText: '3 months free',
  bodyText: 'on any paid plan',
  countdownLabel: 'Introductory pricing · ends soon',
  expiryDate: '',
};

@Component({
  selector: 'app-subscription-management',
  templateUrl: './subscription-management.component.html',
  styleUrls: ['./subscription-management.component.scss'],
})
export class SubscriptionManagementComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  plans: AdminPlan[] = [];
  filteredPlans: AdminPlan[] = [];
  loading = false;

  searchTerm: string = '';
  statusFilter: 'all' | 'active' | 'disabled' = 'all';
  sortField: keyof AdminPlan = 'name';

  currentPage = 1;
  readonly pageSize = 10;

  // ── Plan modal ────────────────────────────────────────────
  showModal = false;
  isEditing = false;
  selectedPlanId: string | null = null;
  modalPlan: Partial<AdminPlan> & { [key: string]: any } = {};

  // ── Offer modal ───────────────────────────────────────────
  showOfferModal = false;
  offerSaving = false;
  platformOffer: PlatformOffer = { ...OFFER_DEFAULTS };

  planStats: PlanStats = {
    mrr: 0,
    totalVendors: 0,
    freeVendors: 0,
    paidVendors: 0,
    vendorsNearLimit: 0,
    gracePeriodAccounts: 0,
  };

  readonly colorPresets = [
    '#6366f1', '#8b5cf6', '#0ea5e9', '#10b981',
    '#f59e0b', '#ef4444', '#64748b', '#14b8a6',
  ];

  workerSettings = {
    enabled: true,
    defaultWorkerLimit: 2
  };

  workerStats = {
    totalWorkers: 0,
    vendorsCount: 0
  };

  constructor(private planService: SubscriptionPlanService) { }

  ngOnInit(): void {
    this.planService.getPlans()
      .pipe(takeUntil(this.destroy$))
      .subscribe(plans => {
        this.plans = plans;
        this.computeStats();
        this.filterPlans();
      });

    this.loadOffer();
    this.loadWorkerSettings();
    this.loadWorkerStats();
  }

  async loadWorkerSettings() {
    try {
      this.workerSettings = await this.planService.getWorkerSettings();
    } catch (e) {
      console.error('Error loading worker settings:', e);
    }
  }

  async loadWorkerStats() {
    try {
      this.workerStats = await this.planService.getWorkerStats();
    } catch (e) {
      console.error('Error loading worker stats:', e);
    }
  }

  async saveWorkerSettings() {
    try {
      if (this.workerSettings.defaultWorkerLimit < 1) {
        this.workerSettings.defaultWorkerLimit = 1;
      }
      this.workerSettings.defaultWorkerLimit = Math.floor(this.workerSettings.defaultWorkerLimit);
      await this.planService.saveWorkerSettings(this.workerSettings);
    } catch (e) {
      console.error('Error saving worker settings:', e);
      alert('Failed to save worker settings.');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Stats ─────────────────────────────────────────────────

  private computeStats(): void {
    const paidPlans = this.plans.filter(p => p.monthlyFee > 0);
    const freePlan = this.plans.find(p => p.monthlyFee === 0);
    const freeCount = freePlan?.subscriberCount ?? 0;
    const paidCount = paidPlans.reduce((s, p) => s + p.subscriberCount, 0);

    this.planStats = {
      mrr: paidPlans.reduce((s, p) => s + p.monthlyFee * p.subscriberCount, 0),
      totalVendors: freeCount + paidCount,
      freeVendors: freeCount,
      paidVendors: paidCount,
      // These two come from the backend — default 0 until a dedicated endpoint exists
      vendorsNearLimit: 0,
      gracePeriodAccounts: 0,
    };
  }

  // ── Offer modal ───────────────────────────────────────────

  private async loadOffer(): Promise<void> {
    try {
      const offer = await this.planService.getPlatformOffer();
      if (offer) this.platformOffer = { ...OFFER_DEFAULTS, ...offer };
    } catch (err) {
      console.error('Could not load platform offer:', err);
    }
  }

  openOfferModal(): void { this.showOfferModal = true; }
  closeOfferModal(): void { this.showOfferModal = false; }

  async saveOffer(): Promise<void> {
    this.offerSaving = true;
    try {
      await this.planService.savePlatformOffer(this.platformOffer);
      this.closeOfferModal();
    } catch (err) {
      console.error('Could not save platform offer:', err);
      alert('Could not save offer. Please try again.');
    } finally {
      this.offerSaving = false;
    }
  }

  resetOffer(): void {
    this.platformOffer = { ...OFFER_DEFAULTS, enabled: this.platformOffer.enabled };
  }

  // ── Plan CRUD ─────────────────────────────────────────────

  async createPlan(): Promise<void> {
    if (!this.modalPlan['name']?.trim()) { alert('Plan name is required.'); return; }
    this.loading = true;
    try {
      await this.planService.createPlan({
        name: this.modalPlan['name'],
        tagline: this.modalPlan['tagline'] ?? '',
        color: this.modalPlan['color'] ?? '#6366f1',
        tag: this.modalPlan['tag'] ?? '',
        icon: this.modalPlan['icon'] ?? 'fa-box',
        monthlyFee: this.modalPlan['monthlyFee'] ?? 0,
        listingFee: this.modalPlan['listingFee'] ?? 0,
        activeListingLimit: this.modalPlan['activeListingLimit'] ?? 0,
        bookingLimit: this.modalPlan['bookingLimit'] ?? 0,
        addOnBookingEnabled: this.modalPlan['addOnBookingEnabled'] ?? false,
        addOnBookingPrice: this.modalPlan['addOnBookingPrice'] ?? 40,
        addOnBookingCount: this.modalPlan['addOnBookingCount'] ?? 10,
        marketplaceEnabled: this.modalPlan['marketplaceEnabled'] ?? false,
        listedEquipmentModuleEnabled: this.modalPlan['listedEquipmentModuleEnabled'] ?? true,
        listedEquipmentCap: this.modalPlan['listedEquipmentCap'] ?? 0,
        listingDurationDays: this.modalPlan['listingDurationDays'] ?? 30,
        manualBookingCycleDays: this.modalPlan['manualBookingCycleDays'] ?? 30,
        gracePeriodDays: this.modalPlan['gracePeriodDays'] ?? 5,
        trialEnabled: this.modalPlan['trialEnabled'] ?? false,
        trialDays: this.modalPlan['trialDays'] ?? 0,
        trialVehicleCap: this.modalPlan['trialVehicleCap'] ?? 200,
        listingExpiryReminderEnabled: this.modalPlan['listingExpiryReminderEnabled'] ?? true,
        listingExpiryReminderDays: this.modalPlan['listingExpiryReminderDays'] ?? 3,
        storageLimitGb: this.modalPlan['storageLimitGb'] ?? 0,
        quarterlyDiscount: this.modalPlan['quarterlyDiscount'] ?? 5,
        halfYearlyDiscount: this.modalPlan['halfYearlyDiscount'] ?? 10,
        yearlyDiscount: this.modalPlan['yearlyDiscount'] ?? 15,
        documentArchiveMonths: this.modalPlan['documentArchiveMonths'] ?? 6,
        isActive: true,
      });
      this.closeModal();
    } catch (err) {
      console.error('Failed to create plan:', err);
      alert('Could not create plan. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  async updatePlan(): Promise<void> {
    if (!this.selectedPlanId) return;
    if (!this.modalPlan['name']?.trim()) { alert('Plan name is required.'); return; }
    this.loading = true;
    try {
      await this.planService.updatePlan(this.selectedPlanId, {
        name: this.modalPlan['name'],
        tagline: this.modalPlan['tagline'],
        color: this.modalPlan['color'],
        tag: this.modalPlan['tag'],
        icon: this.modalPlan['icon'],
        monthlyFee: this.modalPlan['monthlyFee'],
        listingFee: this.modalPlan['listingFee'],
        activeListingLimit: this.modalPlan['activeListingLimit'],
        bookingLimit: this.modalPlan['bookingLimit'],
        addOnBookingEnabled: this.modalPlan['addOnBookingEnabled'],
        addOnBookingPrice: this.modalPlan['addOnBookingPrice'],
        addOnBookingCount: this.modalPlan['addOnBookingCount'],
        marketplaceEnabled: this.modalPlan['marketplaceEnabled'],
        listedEquipmentModuleEnabled: this.modalPlan['listedEquipmentModuleEnabled'],
        listedEquipmentCap: this.modalPlan['listedEquipmentCap'],
        listingDurationDays: this.modalPlan['listingDurationDays'],
        manualBookingCycleDays: this.modalPlan['manualBookingCycleDays'],
        gracePeriodDays: this.modalPlan['gracePeriodDays'],
        trialEnabled: this.modalPlan['trialEnabled'],
        trialDays: this.modalPlan['trialDays'],
        trialVehicleCap: this.modalPlan['trialVehicleCap'],
        listingExpiryReminderEnabled: this.modalPlan['listingExpiryReminderEnabled'],
        listingExpiryReminderDays: this.modalPlan['listingExpiryReminderDays'],
        storageLimitGb: this.modalPlan['storageLimitGb'],
        quarterlyDiscount: this.modalPlan['quarterlyDiscount'],
        halfYearlyDiscount: this.modalPlan['halfYearlyDiscount'],
        yearlyDiscount: this.modalPlan['yearlyDiscount'],
        documentArchiveMonths: this.modalPlan['documentArchiveMonths'],
        isActive: this.modalPlan['isActive'],
      });
      this.closeModal();
    } catch (err) {
      console.error('Failed to update plan:', err);
      alert('Could not update plan. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  async deletePlan(id: string): Promise<void> {
    if (!confirm('Delete this plan? This cannot be undone.')) return;
    try {
      await this.planService.deletePlan(id);
    } catch (err) {
      console.error('Failed to delete plan:', err);
      alert('Could not delete plan. Please try again.');
    }
  }

  async togglePlanStatus(plan: AdminPlan): Promise<void> {
    try {
      await this.planService.updatePlan(plan.id, { isActive: !plan.isActive });
    } catch (err) {
      console.error('Failed to toggle plan status:', err);
      alert('Could not update plan status. Please try again.');
    }
  }

  async duplicatePlan(plan: AdminPlan): Promise<void> {
    try {
      const { id, subscriberCount, lastUpdated, createdAt, ...rest } = plan;
      await this.planService.createPlan({
        ...rest,
        name: `${plan.name} (Copy)`,
        isActive: false,
      });
    } catch (err) {
      console.error('Failed to duplicate plan:', err);
      alert('Could not duplicate plan. Please try again.');
    }
  }

  // ── Plan modal helpers ────────────────────────────────────

  openCreateModal(): void {
    this.isEditing = false;
    this.selectedPlanId = null;
    this.modalPlan = {
      name: '',
      tagline: '',
      color: '#6366f1',
      tag: '',
      icon: 'fa-box',
      monthlyFee: 0,
      listingFee: 0,
      activeListingLimit: 0,
      bookingLimit: 0,
      addOnBookingEnabled: false,
      addOnBookingPrice: 40,
      addOnBookingCount: 10,
      marketplaceEnabled: true,
      listedEquipmentModuleEnabled: true,
      listedEquipmentCap: 0,
      listingDurationDays: 30,
      manualBookingCycleDays: 30,
      gracePeriodDays: 5,
      trialEnabled: false,
      trialDays: 14,
      trialVehicleCap: 200,
      listingExpiryReminderEnabled: true,
      listingExpiryReminderDays: 3,
      storageLimitGb: 0,
      quarterlyDiscount: 5,
      halfYearlyDiscount: 10,
      yearlyDiscount: 15,
      documentArchiveMonths: 6,
    };
    this.showModal = true;
  }

  openEditModal(plan: AdminPlan): void {
    this.isEditing = true;
    this.selectedPlanId = plan.id;
    this.modalPlan = { ...plan };
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedPlanId = null;
    this.modalPlan = {};
  }

  setModalColor(c: string): void { this.modalPlan['color'] = c; }

  // ── Filter / Sort / Paginate ──────────────────────────────

  filterPlans(): void {
    let result = [...this.plans];
  
    const term = this.searchTerm.trim().toLowerCase();
    if (term) {
      result = result.filter(p =>
        p.name.toLowerCase().includes(term) ||
        (p.tagline?.toLowerCase().includes(term) ?? false)
      );
    }

    if (this.statusFilter !== 'all') {
      result = result.filter(p => p.isActive === (this.statusFilter === 'active'));
    }

    this.filteredPlans = result;
    this.applySorting();
    this.currentPage = 1;
  }

  sortPlans(): void { this.applySorting(); }

  private applySorting(): void {
    this.filteredPlans.sort((a, b) => {
      const va = (a as any)[this.sortField];
      const vb = (b as any)[this.sortField];
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb);
      return (va as number) - (vb as number);
    });
  }

  // ── Pagination ────────────────────────────────────────────

  get totalPages(): number { return Math.ceil(this.filteredPlans.length / this.pageSize) || 1; }
  get totalPagesArray(): number[] { return Array.from({ length: this.totalPages }, (_, i) => i + 1); }
  get paginatedPlans(): AdminPlan[] {
    const s = (this.currentPage - 1) * this.pageSize;
    return this.filteredPlans.slice(s, s + this.pageSize);
  }
  get pageStart(): number { return this.filteredPlans.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1; }
  get pageEnd(): number { return Math.min(this.currentPage * this.pageSize, this.filteredPlans.length); }

  prevPage(): void { if (this.currentPage > 1) this.currentPage--; }
  nextPage(): void { if (this.currentPage < this.totalPages) this.currentPage++; }
  goToPage(p: number): void { this.currentPage = p; }
}